import { useState, useMemo } from 'react';
import {
  participantesCampanhas,
  getStatusColor,
  getStatusLabel,
  type ParticipanteCampanha,
} from '@/mocks/backoffice/participantesCampanhas';
import { campanhasBackoffice } from '@/mocks/backoffice/campanhasBackoffice';
import { configuracoesCampanhas, valoresPadraoPlataforma } from '@/mocks/backoffice/configuracoesCampanhas';
import { registrarAuditoria } from '@/mocks/backoffice/auditoriaHelpers';

interface PremiadosConfigProps {
  onShowToast: (msg: string, type: 'success' | 'error') => void;
}

type CampoPersonalizado = 'limiteResgateDiario' | 'limiteResgateMensal' | 'taxaProcessamentoPix' | 'prazoAprovacaoSaque';

interface CampoLoteConfig {
  chave: CampoPersonalizado;
  label: string;
  tipo: 'numero' | 'porcentagem';
  descricao: string;
}

const camposPersonalizaveis: CampoLoteConfig[] = [
  { chave: 'limiteResgateDiario', label: 'Limite Resgate Diário', tipo: 'numero', descricao: 'Valor máximo de resgate PIX por dia (R$)' },
  { chave: 'limiteResgateMensal', label: 'Limite Resgate Mensal', tipo: 'numero', descricao: 'Valor máximo de resgate PIX por mês (R$)' },
  { chave: 'taxaProcessamentoPix', label: 'Taxa Processamento PIX', tipo: 'porcentagem', descricao: 'Taxa percentual cobrada sobre cada resgate PIX' },
  { chave: 'prazoAprovacaoSaque', label: 'Prazo Aprovação Saque', tipo: 'numero', descricao: 'Prazo máximo em horas para aprovação de saques' },
];

export default function PremiadosConfig({ onShowToast }: PremiadosConfigProps) {
  const [participantes, setParticipantes] = useState<ParticipanteCampanha[]>([...participantesCampanhas]);
  const [campanhaSelecionada, setCampanhaSelecionada] = useState<string>('');
  const [busca, setBusca] = useState('');
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [mostrarModalLote, setMostrarModalLote] = useState(false);
  const [mostrarModalIndividual, setMostrarModalIndividual] = useState(false);
  const [participanteEditando, setParticipanteEditando] = useState<ParticipanteCampanha | null>(null);
  const [loteValores, setLoteValores] = useState<Record<string, string>>();

  const participantesFiltrados = useMemo(() => {
    let lista = participantes;
    if (campanhaSelecionada) {
      lista = lista.filter((p) => p.campanhaId === campanhaSelecionada);
    }
    if (busca) {
      const b = busca.toLowerCase();
      lista = lista.filter(
        (p) => p.nome.toLowerCase().includes(b) || p.email.toLowerCase().includes(b) || p.cpf.includes(b) || p.id.toLowerCase().includes(b)
      );
    }
    return lista;
  }, [participantes, campanhaSelecionada, busca]);

  const stats = useMemo(() => {
    const lista = campanhaSelecionada ? participantes.filter((p) => p.campanhaId === campanhaSelecionada) : participantes;
    return {
      total: lista.length,
      personalizados: lista.filter((p) => p.configPersonalizada).length,
      ativos: lista.filter((p) => p.status === 'ativo').length,
      selecionadosCount: selecionados.size,
    };
  }, [participantes, campanhaSelecionada, selecionados]);

  const toggleSelecionado = (id: string) => {
    const novo = new Set(selecionados);
    if (novo.has(id)) novo.delete(id);
    else novo.add(id);
    setSelecionados(novo);
  };

  const toggleTodos = () => {
    if (selecionados.size === participantesFiltrados.length) {
      setSelecionados(new Set());
    } else {
      setSelecionados(new Set(participantesFiltrados.map((p) => p.id)));
    }
  };

  const abrirModalIndividual = (participante: ParticipanteCampanha) => {
    setParticipanteEditando(participante);
    setMostrarModalIndividual(true);
  };

  const salvarIndividual = (id: string, valores: Record<string, number | undefined>) => {
    const idx = participantes.findIndex((p) => p.id === id);
    if (idx === -1) return;

    const temPersonalizacao = Object.values(valores).some((v) => v !== undefined);
    participantes[idx].configPersonalizada = temPersonalizacao;
    participantes[idx].limiteResgateDiarioPersonalizado = valores.limiteResgateDiario;
    participantes[idx].limiteResgateMensalPersonalizado = valores.limiteResgateMensal;
    participantes[idx].taxaProcessamentoPixPersonalizada = valores.taxaProcessamentoPix;
    participantes[idx].prazoAprovacaoSaquePersonalizado = valores.prazoAprovacaoSaque;

    setParticipantes([...participantes]);
    setMostrarModalIndividual(false);

    const part = participantes[idx];
    const camposAlterados = Object.entries(valores)
      .filter(([, v]) => v !== undefined)
      .map(([k]) => camposPersonalizaveis.find((c) => c.chave === k)?.label || k)
      .join(', ');

    registrarAuditoria(
      'alterar_configuracao',
      'configuracoes',
      `Personalizou configuração do premiado ${part.nome} (${part.id}) — campos: ${camposAlterados || 'nenhum (resetado)'}`,
      'André Costa',
      'admin',
      part.id,
      'participante'
    );

    onShowToast(`Configuração de ${part.nome} atualizada com sucesso`, 'success');
  };

  const aplicarLote = () => {
    const novos = [...participantes];
    let count = 0;

    selecionados.forEach((id) => {
      const idx = novos.findIndex((p) => p.id === id);
      if (idx === -1) return;
      let alterado = false;

      camposPersonalizaveis.forEach((campo) => {
        const valor = loteValores[campo.chave];
        if (valor && valor !== '') {
          alterado = true;
          switch (campo.chave) {
            case 'limiteResgateDiario': novos[idx].limiteResgateDiarioPersonalizado = Number(valor); break;
            case 'limiteResgateMensal': novos[idx].limiteResgateMensalPersonalizado = Number(valor); break;
            case 'taxaProcessamentoPix': novos[idx].taxaProcessamentoPixPersonalizada = Number(valor); break;
            case 'prazoAprovacaoSaque': novos[idx].prazoAprovacaoSaquePersonalizado = Number(valor); break;
          }
        }
      });

      if (alterado) {
        novos[idx].configPersonalizada = true;
        count++;
      }
    });

    setParticipantes(novos);
    setSelecionados(new Set());
    setMostrarModalLote(false);
    setLoteValores({});

    registrarAuditoria(
      'alterar_configuracao',
      'configuracoes',
      `Aplicou configuração em lote para ${count} participantes da campanha ${campanhaSelecionada}`,
      'André Costa',
      'admin',
      campanhaSelecionada,
      'campanha'
    );

    onShowToast(`Configurações aplicadas em lote para ${count} participantes`, 'success');
  };

  const getValorEfetivo = (participante: ParticipanteCampanha, campo: CampoPersonalizado) => {
    switch (campo) {
      case 'limiteResgateDiario':
        if (participante.limiteResgateDiarioPersonalizado !== undefined) return { valor: participante.limiteResgateDiarioPersonalizado, personalizado: true };
        break;
      case 'limiteResgateMensal':
        if (participante.limiteResgateMensalPersonalizado !== undefined) return { valor: participante.limiteResgateMensalPersonalizado, personalizado: true };
        break;
      case 'taxaProcessamentoPix':
        if (participante.taxaProcessamentoPixPersonalizada !== undefined) return { valor: participante.taxaProcessamentoPixPersonalizada, personalizado: true };
        break;
      case 'prazoAprovacaoSaque':
        if (participante.prazoAprovacaoSaquePersonalizado !== undefined) return { valor: participante.prazoAprovacaoSaquePersonalizado, personalizado: true };
        break;
    }
    // Busca da campanha
    const configCampanha = configuracoesCampanhas.find((c) => c.campanhaId === participante.campanhaId);
    if (configCampanha && !configCampanha.herdaPlataforma) {
      switch (campo) {
        case 'limiteResgateDiario': return { valor: configCampanha.limites.limite_resgate_diario, personalizado: false };
        case 'limiteResgateMensal': return { valor: configCampanha.limites.limite_resgate_mensal, personalizado: false };
        case 'taxaProcessamentoPix': return { valor: configCampanha.taxas.taxa_processamento_pix, personalizado: false };
        case 'prazoAprovacaoSaque': return { valor: configCampanha.regras.prazo_aprovacao_saque, personalizado: false };
      }
    }
    // Padrão plataforma
    switch (campo) {
      case 'limiteResgateDiario': return { valor: valoresPadraoPlataforma.limites.limite_resgate_diario, personalizado: false };
      case 'limiteResgateMensal': return { valor: valoresPadraoPlataforma.limites.limite_resgate_mensal, personalizado: false };
      case 'taxaProcessamentoPix': return { valor: valoresPadraoPlataforma.taxas.taxa_processamento_pix, personalizado: false };
      case 'prazoAprovacaoSaque': return { valor: valoresPadraoPlataforma.regras.prazo_aprovacao_saque, personalizado: false };
    }
    return { valor: 0, personalizado: false };
  };

  return (
    <div className="space-y-5">
      {/* Filtros e stats */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="lg:w-72">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Campanha</label>
            <select
              value={campanhaSelecionada}
              onChange={(e) => { setCampanhaSelecionada(e.target.value); setSelecionados(new Set()); }}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-white"
            >
              <option value="">Todas as campanhas</option>
              {campanhasBackoffice.map((c) => (
                <option key={c.id} value={c.id}>{c.nome}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Buscar premiado</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-gray-400 text-sm"></i>
              </div>
              <input
                type="text"
                placeholder="Nome, email, CPF ou ID..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-4 text-xs">
          <span className="text-gray-500"><span className="font-bold text-gray-800">{stats.total}</span> participantes</span>
          <span className="text-gray-500"><span className="font-bold text-amber-600">{stats.personalizados}</span> com config. personalizada</span>
          <span className="text-gray-500"><span className="font-bold text-emerald-600">{stats.ativos}</span> ativos</span>
          {stats.selecionadosCount > 0 && (
            <span className="text-emerald-600 font-bold"><span className="font-bold">{stats.selecionadosCount}</span> selecionados</span>
          )}
        </div>
      </div>

      {/* Ações em lote */}
      {stats.selecionadosCount > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-stack-line text-emerald-600 text-sm"></i></div>
            </div>
            <p className="text-sm font-bold text-emerald-800">{stats.selecionadosCount} participantes selecionados</p>
          </div>
          <button
            onClick={() => setMostrarModalLote(true)}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
          >
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-stack-line text-sm"></i></div>
              <span>Configurar em Lote</span>
            </div>
          </button>
        </div>
      )}

      {/* Tabela */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={participantesFiltrados.length > 0 && selecionados.size === participantesFiltrados.length}
                    onChange={toggleTodos}
                    className="w-4 h-4 rounded border-gray-300 text-emerald-600"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Premiado</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Limite Diário</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Limite Mensal</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Taxa PIX</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Prazo Aprovação</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Pontuação</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody>
              {participantesFiltrados.map((p) => {
                const limiteDia = getValorEfetivo(p, 'limiteResgateDiario');
                const limiteMes = getValorEfetivo(p, 'limiteResgateMensal');
                const taxaPix = getValorEfetivo(p, 'taxaProcessamentoPix');
                const prazo = getValorEfetivo(p, 'prazoAprovacaoSaque');
                return (
                  <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-all">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selecionados.has(p.id)}
                        onChange={() => toggleSelecionado(p.id)}
                        className="w-4 h-4 rounded border-gray-300 text-emerald-600"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${p.configPersonalizada ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                          {p.nome.split(' ').map((n) => n[0]).slice(0, 2).join('')}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-800">{p.nome}</p>
                          <p className="text-[10px] text-gray-400">{p.id} · {p.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold ${getStatusColor(p.status)}`}>{getStatusLabel(p.status)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold ${limiteDia.personalizado ? 'text-amber-600' : 'text-gray-700'}`}>
                        R$ {limiteDia.valor.toLocaleString('pt-BR')}
                      </span>
                      {limiteDia.personalizado && <span className="text-[9px] text-amber-500 ml-1">(custom)</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold ${limiteMes.personalizado ? 'text-amber-600' : 'text-gray-700'}`}>
                        R$ {limiteMes.valor.toLocaleString('pt-BR')}
                      </span>
                      {limiteMes.personalizado && <span className="text-[9px] text-amber-500 ml-1">(custom)</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold ${taxaPix.personalizado ? 'text-amber-600' : 'text-gray-700'}`}>
                        {taxaPix.valor}%
                      </span>
                      {taxaPix.personalizado && <span className="text-[9px] text-amber-500 ml-1">(custom)</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold ${prazo.personalizado ? 'text-amber-600' : 'text-gray-700'}`}>
                        {prazo.valor}h
                      </span>
                      {prazo.personalizado && <span className="text-[9px] text-amber-500 ml-1">(custom)</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs">
                        <span className="font-bold text-gray-800">{p.pontosDisponiveis.toLocaleString('pt-BR')}</span>
                        <span className="text-gray-400"> / {p.pontosAcumulados.toLocaleString('pt-BR')}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => abrirModalIndividual(p)}
                        className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-400 hover:text-emerald-600 transition-all cursor-pointer"
                        title="Editar configuração individual"
                      >
                        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-settings-3-line text-sm"></i></div>
                      </button>
                    </td>
                  </tr>
                );
              })}
              {participantesFiltrados.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-gray-400 text-sm">
                    Nenhum participante encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Mostrando <span className="font-bold">{participantesFiltrados.length}</span> de <span className="font-bold">{participantes.length}</span> participantes
          </p>
        </div>
      </div>

      {/* Modal configuração individual */}
      {mostrarModalIndividual && participanteEditando && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-sm font-bold text-emerald-700">
                  {participanteEditando.nome.split(' ').map((n) => n[0]).slice(0, 2).join('')}
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">{participanteEditando.nome}</h3>
                  <p className="text-xs text-gray-500">{participanteEditando.id} · {participanteEditando.email}</p>
                </div>
              </div>
              <button onClick={() => setMostrarModalIndividual(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <div className="w-6 h-6 flex items-center justify-center"><i className="ri-close-line text-xl"></i></div>
              </button>
            </div>

            <p className="text-xs text-gray-500 mb-4">
              Deixe um campo em branco para usar o valor da campanha/plataforma. Preencha para sobrescrever individualmente.
            </p>

            <div className="space-y-4">
              {camposPersonalizaveis.map((campo) => {
                const currentVal =
                  campo.chave === 'limiteResgateDiario' ? participanteEditando.limiteResgateDiarioPersonalizado :
                  campo.chave === 'limiteResgateMensal' ? participanteEditando.limiteResgateMensalPersonalizado :
                  campo.chave === 'taxaProcessamentoPix' ? participanteEditando.taxaProcessamentoPixPersonalizada :
                  participanteEditando.prazoAprovacaoSaquePersonalizado;
                const efetivo = getValorEfetivo(participanteEditando, campo.chave);

                return (
                  <div key={campo.chave}>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">{campo.label}</label>
                    <p className="text-[10px] text-gray-400 mb-1">{campo.descricao}</p>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        defaultValue={currentVal ?? ''}
                        placeholder={`Padrão: ${efetivo.valor}`}
                        onChange={(e) => {
                          const val = e.target.value;
                          setLoteValores((prev) => ({ ...prev, [campo.chave]: val }));
                        }}
                        className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                        step={campo.tipo === 'porcentagem' ? '0.01' : '1'}
                      />
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {campo.tipo === 'porcentagem' ? '%' : campo.chave.includes('prazo') ? 'h' : 'R$'}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">Valor da campanha: {campo.tipo === 'porcentagem' ? `${efetivo.valor}%` : `R$ ${efetivo.valor.toLocaleString('pt-BR')}`}</p>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-100">
              <button onClick={() => setMostrarModalIndividual(false)} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap">Cancelar</button>
              <button
                onClick={() => {
                  const valores: Record<string, number | undefined> = {};
                  camposPersonalizaveis.forEach((campo) => {
                    const val = loteValores[campo.chave];
                    valores[campo.chave] = val && val !== '' ? Number(val) : undefined;
                  });
                  salvarIndividual(participanteEditando.id, valores);
                }}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
              >
                Salvar Configuração
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal aplicação em lote */}
      {mostrarModalLote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Configurar em Lote</h3>
              <button onClick={() => { setMostrarModalLote(false); setLoteValores({}); }} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <div className="w-6 h-6 flex items-center justify-center"><i className="ri-close-line text-xl"></i></div>
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Configurações serão aplicadas para <strong>{stats.selecionadosCount}</strong> participantes selecionados.
              Deixe um campo em branco para manter o valor atual de cada participante.
            </p>
            <div className="space-y-4">
              {camposPersonalizaveis.map((campo) => (
                <div key={campo.chave}>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">{campo.label}</label>
                  <p className="text-[10px] text-gray-400 mb-1">{campo.descricao}</p>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      value={loteValores[campo.chave] || ''}
                      onChange={(e) => setLoteValores((prev) => ({ ...prev, [campo.chave]: e.target.value }))}
                      placeholder="Manter atual"
                      className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                      step={campo.tipo === 'porcentagem' ? '0.01' : '1'}
                    />
                    <span className="text-xs text-gray-400">{campo.tipo === 'porcentagem' ? '%' : campo.chave.includes('prazo') ? 'h' : 'R$'}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-100">
              <button onClick={() => { setMostrarModalLote(false); setLoteValores({}); }} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={aplicarLote} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-stack-line text-sm"></i></div>
                  <span>Aplicar para {stats.selecionadosCount}</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}